import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BsThreeDots, BsFillFilterCircleFill } from 'react-icons/bs';
import { FaTrash, FaStar, FaRegStar } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
// Trash icon imported above
import {
  removeAIContext,
  removeGraphItem,
  setResetView,
  setSearch,
  setView,
  getRelationshipSuggestions,
} from 'store/graph/actions';
import { deleteContextFilters, setViewLoading } from 'store/filters/actions';
import styles from './context-input.module.css';
import useComponentVisible from 'utils/hooks';
import ReactTooltip from 'react-tooltip';
import { setViewFilters } from 'store/filters/actions';
import { setShowIndividualFilters, toggleFavorite } from 'store/core/actions';
import { selectContextFilters } from 'store/filters/selectors';
import { selectIsFavorite } from 'store/core/selectors';
import { IsObjectEmpty } from 'utils/graph-helper';
import { selectAiContexts } from 'store/graph/selectors';
import SuggestionTrigger from '../suggestion-trigger';
import SuggestionsPopup from '../suggestions-popup';

const ContextInputItem = ({ context, contextLength }) => {
  const dispatch = useDispatch();
  const contextFilters = useSelector(selectContextFilters(context.id));
  const aiContext = useSelector(selectAiContexts(context.id));
  const isFavorite = useSelector(selectIsFavorite(context.id));
  const { ref, isVisible, setIsVisible } = useComponentVisible(false);
  const {
    ref: popupRef,
    isVisible: isPopupVisible,
    setIsVisible: setIsPopupVisible,
  } = useComponentVisible(false);
  const triggerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState(null);
  // Render simplified chip for filter session contexts
  if (context.label === 'filter_session') {
    const resultCount = context.resultCount || context.nodeCount || 0;
    const filterSummary = context.displayValue || 'Filter Session';

    const removeFilterSession = () => {
      dispatch(removeGraphItem(context.id));
    };

    return (
      <div key={context.id}>
        <div
          className={styles.input_wrapper}
          style={{ background: 'rgba(56, 178, 217, 0.1)', borderLeft: '3px solid #38b2d9' }}
          key={context.id}
        >
          <span style={{ fontSize: '14px', marginRight: '6px' }} role="img" aria-label="search">🔍</span>
          <input
            className={styles.search_input}
            type="text"
            value={filterSummary}
            readOnly
            style={{ color: '#38b2d9', fontWeight: 600 }}
          />
          <div className={styles.node_count} style={{ background: '#38b2d9', color: '#fff', borderRadius: '10px', padding: '0 6px', minWidth: '20px', textAlign: 'center' }}>
            {resultCount}
          </div>
          <FaTrash className={styles.remove_target} onClick={removeFilterSession} />
        </div>
        <div className={styles.vertical_line} />
      </div>
    );
  }

  let value = context.value ?? context.displayValue;
  const view = context.currentView;
  const label = context.label;

  let employersViewName = 'LargestEmployers';
  let largestFunds = 'TopPhilanthropicInterests';
  let largestEngagement = 'HighestEngagement';
  const isGeoContext = !!context.geoType || context.label === 'region';
  if (isGeoContext) {
    employersViewName = 'TopRegion25LargestEmployers';
    largestFunds = 'TopRegion25LargestFunds';
    largestEngagement = 'TopRegion25LargestEngagement';
    // Prefer display-friendly name for regions
    value = context.displayValue ?? context.value ?? context.metroRegion;
  }

  const removeContext = () => {
    dispatch(deleteContextFilters(context.id));
    dispatch(removeGraphItem(context.id));
    if (context.view !== undefined || context.currentView) {
      dispatch(setViewFilters('All'));
    }
  };

  const showViewMenu = () => {
    setIsVisible(true);
  };
  const hideViewMenu = () => {
    setIsVisible(false);
  };

  const handleAll = () => {
    dispatch(setResetView(context));
    dispatch(setViewFilters('All'));
    hideViewMenu();
  };

  const handleFilters = () => {
    dispatch(setShowIndividualFilters(context.id));
  };

  const handleAiContext = () => {
    removeContext();
    dispatch(setSearch(aiContext));
    dispatch(removeAIContext(aiContext.id));
  };

  const handleFavorite = () => {
    // For regions: use composite key (type|value) to uniquely identify the geo item
    // For nodes: use the node ID
    const isRegion = !!context.geoType; // Check for geoType to identify geographic items
    const favoriteId = isRegion ? `${context.geoType}|${context.geoValue}` : context.id;
    const favorite = {
      nodeId: favoriteId,
      displayName: value,
      nodeType: context.label,
      metroRegion: context.metroRegion,
      blobUrl: context.blobUrl,
      addedAt: new Date().toISOString(),
      context: context,
    };
    dispatch(toggleFavorite(favorite));
  };

  const handleFetchSuggestions = (personId, signals) => {
    dispatch(getRelationshipSuggestions(personId, signals));
    // Don't close popup - let it show loading state, then close when suggestions arrive
  };

  const calculatePopupPosition = (rect) => {
    const POPUP_WIDTH = 320;
    const MARGIN = 8;
    
    const left = Math.min(
      Math.max(rect.left, MARGIN),
      window.innerWidth - POPUP_WIDTH - MARGIN,
    );
    const top = rect.bottom + MARGIN;
    
    return { top, left };
  };

  const openSuggestionsPopup = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    
    if (rect) {
      setPopupPosition(calculatePopupPosition(rect));
    } else {
      setPopupPosition(null);
    }
    
    setIsPopupVisible(true);
  };

  return (
    <div key={context.id}>
      <div
        className={`${styles.input_wrapper} ${
          isVisible ? styles.bottomBorder : ''
        }`}
        key={context.id}
      >
        <input
          className={styles.search_input}
          type="text"
          data-tip
          data-for={`context-id-${context.id}`}
          value={value ?? ''}
          readOnly
        />
        <ReactTooltip
          id={`context-id-${context.id}`}
          place="top"
          effect="solid"
        >
          {value}
        </ReactTooltip>
        {aiContext ? (
          <div className={styles.ai_context} onClick={handleAiContext}>
            <button className={styles.ai_context_button}>Clear AI</button>
            <ReactTooltip id="ai-context" place="top" effect="solid">
              Remove AI Context
            </ReactTooltip>
          </div>
        ) : (
          <>
            {label === 'person' && (
              <div ref={popupRef} className={styles.suggestions_anchor}>
                <span ref={triggerRef} className={styles.suggestions_trigger}>
                  <SuggestionTrigger onClick={openSuggestionsPopup} />
                </span>
                {isPopupVisible && (
                  <SuggestionsPopup
                    person={context}
                    onClose={() => setIsPopupVisible(false)}
                    onFetchSuggestions={handleFetchSuggestions}
                    position={popupPosition}
                  />
                )}
              </div>
            )}
            <div className={styles.node_count}>{context.nodeCount}</div>
            {!IsObjectEmpty(contextFilters) && (
              <div className={styles.remove_target} onClick={handleFilters}>
                <BsFillFilterCircleFill
                  data-tip
                  data-for={`${value}-filters`}
                  className={styles.remove_target}
                />
                <ReactTooltip
                  id={`${value}-filters`}
                  place="top"
                  effect="solid"
                >
                  Individual Filters Enabled
                </ReactTooltip>
              </div>
            )}
            <div className={styles.favorite_icon} onClick={handleFavorite}>
              {isFavorite ? <FaStar color="#FFD700" /> : <FaRegStar />}
            </div>
            <FaTrash className={styles.remove_target} onClick={removeContext} />
            <BsThreeDots onClick={showViewMenu} className={styles.icon} />
          </>
        )}
      </div>
      {isVisible && (
        <div ref={ref} className={styles.dropdownCard}>
          <div onClick={removeContext} className={styles.dropDownItem}>
            Remove Target
          </div>
          {view && (
            <div onClick={handleAll} className={styles.dropDownItem}>
              View All
            </div>
          )}
          {label === 'person' && (
            <>
              <Item
                view="Family-Interests"
                selectedView={view}
                text="View Family Interests"
                context={context}
                hideViewMenu={hideViewMenu}
                numOfContexts={contextLength}
              />
              <Item
                view="Colleagues"
                selectedView={view}
                text="View Colleagues"
                context={context}
                hideViewMenu={hideViewMenu}
                numOfContexts={contextLength}
              />
              <Item
                view="Classmates"
                selectedView={view}
                text="View Classmates"
                context={context}
                hideViewMenu={hideViewMenu}
                numOfContexts={contextLength}
              />
              {/* <Item
                view="Advancement-Connections"
                selectedView={view}
                text="View Advancement Connections"
                context={context}
                hideViewMenu={hideViewMenu}
              /> */}
            </>
          )}
          {label !== 'fund' && (
            <Item
              view={largestFunds}
              selectedView={view}
              text="View Top 25 Funds/Charitable Organizations View"
              context={context}
              hideViewMenu={hideViewMenu}
              numOfContexts={contextLength}
            />
          )}
          {label !== 'organization' && (
            <Item
              view={employersViewName}
              selectedView={view}
              text="View Top 25 Employers View"
              context={context}
              hideViewMenu={hideViewMenu}
              numOfContexts={contextLength}
            />
          )}
          {label !== 'engagement' && (
            <Item
              view={largestEngagement}
              selectedView={view}
              text="View Top 25 Engagements View"
              context={context}
              hideViewMenu={hideViewMenu}
              numOfContexts={contextLength}
            />
          )}
          {/* {label === 'education' && <Item
            view="Education-Context"
            text="View Education Context"
            selectedView={view}
            context={context}
            hideViewMenu={hideViewMenu}
          />} */}
        </div>
      )}
      <div className={styles.vertical_line} />
    </div>
  );
};

ContextInputItem.propTypes = {
  context: PropTypes.object,
};

const Item = ({
  view,
  selectedView,
  text,
  context,
  hideViewMenu,
  contextLength,
}) => {
  const dispatch = useDispatch();
  const handleView = () => {
    dispatch(setView(view, context));
    dispatch(setViewFilters(view, contextLength > 1));
    dispatch(setViewLoading(context));
    hideViewMenu();
  };

  return (
    <div
      onClick={handleView}
      className={`${styles.dropDownItem} ${
        view === selectedView ? styles.dropDownItemSelected : ''
      }`}
      data-tip
      data-for="context-view"
    >
      {text}
      <ReactTooltip id="context-view" place="top" effect="solid">
        this action will reset your current filters
      </ReactTooltip>
    </div>
  );
};

export default ContextInputItem;
